import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import useGcpsUIController from "@/hooks/useGlobalContext";
import { api } from "@/lib/api";
import { toaster } from "./Toaster";
import { Modal } from "./Modal";
import { Form, FormControl, FormDescription, FormField, FormItem, FormMessage } from "./ui/form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { handleMutationError } from "@/lib/utils";

const formSchema = z
	.object({
		old_password: z.string().min(1, "Old password is required"),
		password: z
			.string()
			.min(8, "Password must be at least 8 characters long")
			.regex(
				/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).+$/,
				"Password must contain at least one uppercase letter, lowercase letter, number, and special character"
			),
		password_confirmation: z.string().min(1, "Confirm password is required"),
	})
	.refine((data) => data.password === data.password_confirmation, {
		message: "Passwords do not match",
		path: ["password_confirmation"],
	});

type FormType = z.infer<typeof formSchema>;

export default function ForcePasswordReset() {
	const { controller } = useGcpsUIController();
	const { resident } = controller;

	const form = useForm<FormType>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			old_password: "zEY5APiC%",
			password: "",
			password_confirmation: "",
		},
	});

	async function resetPassword(values: FormType) {
		const res = await api.post("auth/change-password", values);
		return res.data;
	}

	const { mutate, isPending } = useMutation({
		mutationFn: resetPassword,
		onSuccess: (data) => {
			if (data) {
				toaster("success", "Password reset successfully");
				setTimeout(() => {
					return window && window.location.reload();
				}, 500);
			}
		},
		onError: handleMutationError
	});

	function onSubmit(values: FormType) {
		mutate(values);
	}

	return (
		<Modal
			title="Reset Password"
			open={resident?.force_password_reset || false}
			onOpenChange={() => true}
		>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-1 text-left -mt-6"
				>
					<div className="flex-1 ">
						<FormField
							control={form.control}
							name="old_password"
							render={({ field }) => (
								<FormItem className="hidden">
									<FormControl>
										<Input
											{...field}
											type="password"
											placeholder="Enter old password"
											label="Old password (Default Password)"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className="flex-1 ">
						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<Input
											{...field}
											type="password"
											placeholder="Enter new password"
											label="New Password"
										/>
									</FormControl>
									<FormMessage />
									<FormDescription>Password must be at least 8 characters long and contain at least one uppercase letter, lowercase letter, number, and special character</FormDescription>
								</FormItem>
							)}
						/>
					</div>
					<div className="flex-1 ">
						<FormField
							control={form.control}
							name="password_confirmation"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<Input
											{...field}
											type="password"
											placeholder="Confirm new password"
											label="Confirm New Password"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className="flex justify-end">
						<Button
							type="submit"
							className="mt-4"
							loading={isPending}
							disabled={isPending}
						>
							Reset password
						</Button>
					</div>
				</form>
			</Form>
		</Modal>
	);
}
